import React, { useEffect, useState } from 'react';
import './orderDetails.css';
import ApiService, { initialAuthState } from '../../services/ApiService';
import { useParams, useNavigate } from 'react-router';
import { IoMdArrowRoundBack } from "react-icons/io";

const formatDate = (date) =>
  date ? new Date(date).toLocaleString('en-IN') : '-';

const StatusBadge = ({ status }) => (
  <span className={`admin-order-details-status-badge ${status?.toLowerCase()}`}>
    {status}
  </span>
);

const AddressBlock = ({ title, data }) => {
  if (!data) return null;

  return (
    <div className="admin-address-block">
      <h4>{title}</h4>
      <p><strong>{data.name}</strong></p>
      <p>{data.phoneNumber}</p>
      <p>{data.addressLineOne}</p>
      {data.addressLineTwo && <p>{data.addressLineTwo}</p>}
      <p>{data.city}, {data.state}</p>
      <p>{data.country} - {data.pincode}</p>
    </div>
  );
};

const OrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [selectedItems, setSelectedItems] = useState([]);
  const [creatingShipment, setCreatingShipment] = useState(false);
  const [packingItemId, setPackingItemId] = useState(null);

  const [showShipmentModal, setShowShipmentModal] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState('');

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleOpenShipmentModal = () => {
    if (selectedItems.length === 0) {
      alert('Select items to create shipment');
      return;
    }

    setShowShipmentModal(true);
  };

  const handleConfirmShipment = async () => {
    if (!deliveryDate) {
      alert('Please select delivery date');
      return;
    }

    try {
      setCreatingShipment(true);

      const res = await ApiService.post('/shipment/auto-create', {
        orderId: Number(orderId),
        orderItemIds: selectedItems,
        companyCode: initialAuthState.companyCode,
        unitCode: initialAuthState.unitCode,
        deliveryDate: deliveryDate,
      });

      if (res.status) {
        alert('Shipment created successfully');
        setSelectedItems([]);
        setDeliveryDate('');
        setShowShipmentModal(false);
        fetchOrder();
      } else {
        alert('Failed to create shipment');
      }
    } catch (err) {
      console.error(err);
      alert('Error creating shipment');
    } finally {
      setCreatingShipment(false);
    }
  };

  const handleCreateShipment = async () => {
    if (selectedItems.length === 0) {
      alert('Select items to create shipment');
      return;
    }

    try {
      setCreatingShipment(true);

      const res = await ApiService.post('/shipment/auto-create', {
        orderId: Number(orderId),
        orderItemIds: selectedItems,
        companyCode: initialAuthState.companyCode,
        unitCode: initialAuthState.unitCode,
      });

      if (res.status) {
        alert('Shipment created successfully');
        setSelectedItems([]);
        fetchOrder(); // refresh data
      } else {
        alert('Failed to create shipment');
      }
    } catch (err) {
      console.error(err);
      alert('Error creating shipment');
    } finally {
      setCreatingShipment(false);
    }
  };

  const fetchOrder = async () => {
    try {
      const res = await ApiService.post('/order/getOrderById', {
        id: Number(orderId),
        companyCode: initialAuthState.companyCode,
        unitCode: initialAuthState.unitCode,
      });

      if (res.status) {
        setOrder(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
  }, []);

  if (loading) return <div className="admin-order-details-loader">Loading...</div>;
  if (!order) return <div className="admin-order-details-error">Order not found</div>;

  const {
    orderNumber,
    orderStatus,
    paymentStatus,
    createdAt,
    client,
    deliveryAddress,
    billingAddress,
    totalAmount,
    subtotalAmount,
    totalTaxAmount,
    shippingCharge,
    items = [],
  } = order;

  // 🔥 Fulfillment Summary
  const totalItems = items.length;
  // const shippedItems = items.filter(i => i.status === 'shipped').length;
  const shippedItems = items.filter(
    item => item.shipmentItems && item.shipmentItems.length > 0
  );

  const COMPLETED = ['delivered', 'replaced'];
  const IN_PROGRESS = [
    'packed',
    'shipped',
    'created',
    'replacement_requested',
    'replacement_approved',
    'pickup_scheduled',
    'picked_up'
  ];
  const PENDING = ['pending', 'confirmed', 'placed'];


  const deliveredItemsCount = items.filter(
    i => COMPLETED.includes(i.itemStatus)
  ).length;

  const inProgressItemsCount = items.filter(
    i => IN_PROGRESS.includes(i.itemStatus)
  ).length;

  const pendingItemsCount = items.filter(
    i => PENDING.includes(i.itemStatus)
  ).length;

  const unshippedItems = items.filter(
    item => !item.shipmentItems || item.shipmentItems.length === 0
  );

  const cancelledOrderItemsCount = items.filter(
    i => i.itemStatus === 'cancelled'
  ).length;

  const packedItemsCount = items.filter(
    i => i.itemStatus === 'packed'
  ).length;


  const packedItems = unshippedItems.filter(
    i => i.itemStatus === 'packed'
  );

  const isAllSelected =
    packedItems.length > 0 &&
    packedItems.every(i => selectedItems.includes(i.id));

  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  console.log(selectedItems, "selected items for shipment")

  const handleSelectAllPacked = () => {
    if (isAllSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(packedItems.map(i => i.id));
    }
  };

  const groupedItems = items.reduce((acc, item) => {
    const branch = item.branch.branchName || 'Unassigned';
    if (!acc[branch]) acc[branch] = [];
    acc[branch].push(item);
    return acc;
  }, {});


  const shipmentsMap = {};

  shippedItems.forEach(item => {
    item.shipmentItems.forEach(si => {
      const shipment = si.shipment;

      if (!shipmentsMap[shipment.id]) {
        shipmentsMap[shipment.id] = {
          ...shipment,

          // ✅ Attach branch from FIRST item
          branch: item.branch || null,

          items: []
        };
      }

      shipmentsMap[shipment.id].items.push({
        ...item,
        shippedQuantity: si.quantity
      });
    });
  });

  const shipments = Object.values(shipmentsMap);

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const handleConfirmItems = async () => {
    try {
      setCreatingShipment(true);

      const placedItems = items.filter(i => i.itemStatus === 'placed');

      if (placedItems.length === 0) {
        alert('No placed items found');
        return;
      }

      await Promise.all(
        placedItems.map(item =>
          ApiService.post('/order-item/adminUpdateStatus', {
            id: item.id,
            status: 'confirmed',
            companyCode: initialAuthState.companyCode,
            unitCode: initialAuthState.unitCode,
          })
        )
      );

      alert('All placed items confirmed');

      setSelectedItems([]);
      fetchOrder(); // 🔥 important refresh

    } catch (err) {
      console.error(err);
      alert('Error confirming items');
    } finally {
      setCreatingShipment(false);
    }
  };

  const hasPlacedItems = items.some(i => i.itemStatus === 'placed');

  const hasConfirmedItems = items.some(i => i.itemStatus === 'confirmed');

  const showConfirmMode = hasPlacedItems;
  const showShipmentMode = !hasPlacedItems && hasConfirmedItems;

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/all-orders');
    }
  };


  return (
    <div className="admin-order-details-page">

      <div className="replace-request-button-container">
        <button onClick={handleBack} className="website-back-button">
          <IoMdArrowRoundBack /> Back
        </button>
      </div>

      {/* 🔷 HEADER */}
      <div className="admin-order-details-card admin-order-details-header">
        <div>
          <h2>Order #{orderNumber}</h2>
          <p>{formatDate(createdAt)}</p>
        </div>

        <div className="admin-order-details-status-group">
          <div className="admin-order-details-status-item">
            <span className="status-label">Order Status</span>
            <StatusBadge status={orderStatus} />
          </div>
          <div className="admin-order-details-status-item">
            <span className="status-label">Payment Status</span>
            <StatusBadge status={paymentStatus} />
          </div>
        </div>
      </div>

      <div className="admin-order-kpi-grid">
        <div className="admin-kpi-card">
          <span>Total Amount</span>
          <h3>₹{totalAmount}</h3>
        </div>

        <div className="admin-kpi-card">
          <span>Total Items</span>
          <h3>{totalItems}</h3>
        </div>

        <div className="admin-kpi-card">
          <span>In Progress</span>
          <h3>{inProgressItemsCount}</h3>
        </div>

        <div className="admin-kpi-card">
          <span>Delivered</span>
          <h3>{deliveredItemsCount}</h3>
        </div>

        <div className="admin-kpi-card">
          <span>Pending</span>
          <h3>{pendingItemsCount}</h3>
        </div>

        <div className="admin-kpi-card">
          <span>Cancelled</span>
          <h3>{cancelledOrderItemsCount}</h3>
        </div>
      </div>

      <div className="admin-order-details-card admin-customer-section">
        <h3>Customer Details</h3>

        <div className="admin-customer-grid">
          <div className="admin-customer-info">
            <p><strong>Name:</strong> {client?.name}</p>
            <p><strong>Phone:</strong> {client?.phoneNumber}</p>
            <p><strong>Email:</strong> {client?.email}</p>
            <p><strong>Client ID:</strong> {client?.clientId}</p>
          </div>

          <div className="admin-address-grid">
            <AddressBlock title="Delivery Address" data={deliveryAddress} />
            <AddressBlock title="Billing Address" data={billingAddress} />
          </div>
        </div>
      </div>

      {/* 🔥 FULFILLMENT OVERVIEW */}
      {/* <div className="admin-order-details-card admin-order-details-fulfillment">
        <h3>Overview</h3>
        <div className="admin-order-details-grid-3">
          <div>Total Items: {totalItems}</div>
          <div>In Progress: {inProgressItemsCount}</div>
          <div>Delivered: {deliveredItemsCount}</div>
          <div>Pending: {pendingItemsCount}</div>
          <div>Cancelled: {cancelledOrderItemsCount}</div>
        </div>
      </div> */}


      {/* 🔷 ITEMS TABLE */}
      <div className="admin-order-details-card">

        <div className='admin-order-details-card-order-items-container'>
          <h3>Order Items</h3>

          <div className="admin-order-details-bulk-actions">

            <button onClick={handleSelectAllPacked}>
              {isAllSelected ? 'Unselect All Packed' : 'Select All Packed'}
            </button>

            {/* <button
              className="admin-order-details-primary-btn"
              disabled={creatingShipment || selectedItems.length === 0}
              onClick={handleOpenShipmentModal}
            >
              {creatingShipment ? 'Creating...' : `Create Shipment (${selectedItems.length})`}
            </button> */}

            {/* 🔵 CONFIRM BUTTON */}
            {showConfirmMode && (
              <button
                className="admin-order-details-primary-btn"
                disabled={creatingShipment}
                onClick={handleConfirmItems}
              >
                Confirm Items (All Placed)
              </button>
            )}

            {/* 🟢 CREATE SHIPMENT BUTTON */}
            {showShipmentMode && (
              <button
                className="admin-order-details-primary-btn"
                disabled={creatingShipment || selectedItems.length === 0}
                onClick={handleOpenShipmentModal}
              >
                Create Shipment ({selectedItems.length})
              </button>
            )}

          </div>
        </div>

        <table className="admin-order-details-items-table">
          <thead>
            <tr>
              {showShipmentMode && <th>Select</th>}
              <th>Item</th>
              <th>Order Item ID</th>
              <th>Qty</th>
              {/* <th>Price</th>
              <th>Tax</th> */}
              <th>Total</th>
              <th>Branch</th>
              <th>Status</th>
              {/* <th>Shipment</th> */}
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {Object.entries(
              unshippedItems.reduce((acc, item) => {
                const branch = item.branch?.branchName || 'Unassigned';
                if (!acc[branch]) acc[branch] = [];
                acc[branch].push(item);
                return acc;
              }, {})
            ).map(([branch, branchItems]) => (

              <React.Fragment key={branch}>

                {/* 🔷 BRANCH HEADER */}
                <tr className="admin-order-details-branch-row">
                  <td colSpan="11">
                    <strong>{branch}</strong> ({branchItems.length} items)
                  </td>
                </tr>

                {branchItems.map((item) => {

                  const isConfirmed = item.itemStatus === 'confirmed';

                  return (
                    <tr key={item.id}>

                      {/* SELECT */}
                      {showShipmentMode && (
                        <td>
                          <input
                            type="checkbox"
                            disabled={!isConfirmed}
                            checked={selectedItems.includes(item.id)}
                            onChange={() => toggleSelectItem(item.id)}
                          />
                        </td>
                      )}

                      {/* ITEM */}
                      <td>
                        <div className="admin-item-cell">
                          <strong>{item.deviceName}</strong>
                          <span className="admin-sub-text">{item.orderItemNumber}</span>
                        </div>
                      </td>

                      <td>{item.id}</td>

                      <td>{item.quantity}</td>

                      {/* <td>₹{item.price}</td>

                      <td>₹{item.totalTaxAmount}</td> */}

                      <td>
                        <strong>₹{item.totalPrice}</strong>
                      </td>

                      <td>{item.branch?.branchName}</td>

                      <td>
                        <StatusBadge status={item.itemStatus} />
                      </td>

                      {/* SHIPMENT */}
                      {/* <td>
                        {item.shipmentItems?.length > 0 ? (
                          <button
                            className="admin-order-details-link-btn"
                            onClick={() =>
                              navigate(`/admin/shipments/${item.shipmentItems[0]?.shipment?.id}`)
                            }
                          >
                            View
                          </button>
                        ) : (
                          <span className="admin-muted">Not Created</span>
                        )}
                      </td> */}

                      {/* ACTIONS */}
                      <td>
                        <button
                          className="admin-order-details-primary-btn"
                          onClick={() => navigate(`/order-item/${item.id}`)}
                        >
                          View
                        </button>
                      </td>

                    </tr>
                  );
                })}

              </React.Fragment>

            ))}
          </tbody>
        </table>
      </div>

      {/* {showShipmentModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">

            <h3>Create Shipment</h3>

            <div className="admin-modal-field">
              <label>Expected Delivery Date</label>
              <input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
              />
            </div>

            <div className="admin-modal-actions">
              <button
                className="admin-order-details-primary-btn"
                onClick={handleConfirmShipment}
                disabled={creatingShipment}
              >
                {creatingShipment ? 'Creating...' : 'Confirm'}
              </button>

              <button
                className="admin-order-details-secondary-btn"
                onClick={() => setShowShipmentModal(false)}
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      )} */}

      {showShipmentModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal amz-modal">

            {/* HEADER */}
            <div className="amz-modal-header">
              <h3>Create Shipment</h3>
              <p>Confirm shipment details before proceeding</p>
            </div>

            {/* 🔷 CONTEXT INFO */}
            {/* <div className="amz-modal-summary">

              <div className="amz-summary-row">
                <span>Order Item</span>
                <strong>{item?.deviceName}</strong>
              </div>

              <div className="amz-summary-row">
                <span>Quantity</span>
                <strong>{item?.quantity}</strong>
              </div>

              <div className="amz-summary-row">
                <span>Branch</span>
                <strong>{item?.branch?.branchName || "Not Assigned"}</strong>
              </div>

              <div className="amz-summary-row">
                <span>Customer</span>
                <strong>{item?.order?.clientName || "N/A"}</strong>
              </div>

            </div> */}

            {/* 🔷 DELIVERY DATE */}
            <div className="admin-modal-field">
              <label>Expected Delivery Date *</label>

              <input
                type="date"
                value={deliveryDate}
                min={new Date().toISOString().split("T")[0]} // ✅ prevent past dates
                onChange={(e) => setDeliveryDate(e.target.value)}
              />

              {!deliveryDate && (
                <small className="field-error">
                  Please select a delivery date
                </small>
              )}
            </div>

            {/* 🔷 WARNING */}
            <div className="amz-warning-box">
              ⚠️ Once shipment is created, invoice will be generated based on these details.
            </div>

            {/* ACTIONS */}
            <div className="admin-modal-actions amz-modal-actions">

              <button
                className="admin-order-details-primary-btn"
                onClick={handleConfirmShipment}
                disabled={!deliveryDate || creatingShipment}
              >
                {creatingShipment ? 'Creating Shipment...' : 'Confirm Shipment'}
              </button>

              <button
                className="admin-order-details-secondary-btn"
                onClick={() => setShowShipmentModal(false)}
              >
                Cancel
              </button>

            </div>

          </div>
        </div>
      )}

      <div className="admin-order-details-card">
        <h3>Shipments</h3>

        {shipments.length === 0 ? (
          <p>No shipments created</p>
        ) : (
          <table className="admin-order-details-items-table">

            <thead>
              <tr>
                <th>Shipment No</th>
                <th>Shipment ID</th>
                <th>Branch</th>
                <th>Status</th>
                <th>Items</th>
                <th>Total Qty</th>
                <th>Total Value</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {shipments.map((shipment) => {

                const totalQty = shipment.items.reduce(
                  (sum, i) => sum + Number(i.shippedQuantity),
                  0
                );

                const totalValue = shipment.items.reduce(
                  (sum, i) => sum + Number(i.totalPrice),
                  0
                );

                return (
                  <tr key={shipment.id}>

                    <td>
                      <strong>{shipment.shipmentNumber}</strong>
                    </td>

                    <td>{shipment.id}</td>

                    <td>{shipment.branch?.branchName}</td>

                    <td>
                      <StatusBadge status={shipment.status} />
                    </td>

                    <td>
                      <div className="admin-shipment-items">
                        {shipment.items.map((item) => (
                          <div key={item.id}>
                            {item.deviceName} (x{item.shippedQuantity})
                          </div>
                        ))}
                      </div>
                    </td>

                    <td>
                      <strong>{totalQty}</strong>
                    </td>

                    <td>
                      <strong>₹{totalValue.toFixed(2)}</strong>
                    </td>

                    <td>
                      <button
                        className="admin-order-details-link-btn"
                        onClick={() =>
                          navigate(`/admin/shipments/${shipment.id}`)
                        }
                      >
                        View Shipment
                      </button>
                    </td>

                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>


      {/* 🔷 PAYMENT DETAILS */}
      <div className="admin-order-details-card admin-payment-section">

        <h3>Payment Details</h3>

        {/* 🔷 SUMMARY BLOCK */}
        <div className="admin-payment-summary">

          <div className="admin-order-details-row">
            <span>Subtotal</span>
            <span>₹{Number(subtotalAmount).toFixed(2)}</span>
          </div>

          <div className="admin-order-details-row">
            <span>Tax</span>
            <span>₹{Number(totalTaxAmount).toFixed(2)}</span>
          </div>

          <div className="admin-order-details-row">
            <span>Shipping Charges</span>
            <span>₹{Number(shippingCharge).toFixed(2)}</span>
          </div>

          <div className="admin-payment-divider" />

          <div className="admin-order-details-row admin-order-details-total">
            <span>Total Paid</span>
            <span>₹{Number(totalAmount).toFixed(2)}</span>
          </div>

        </div>

        {/* 🔷 TRANSACTIONS */}
        <div className="admin-payment-transactions">

          <h4>Transactions</h4>

          {order.transactions && order.transactions.length > 0 ? (
            <div className="admin-table-wrapper">

              <table className="admin-order-details-transactions-table">

                <thead>
                  <tr>
                    <th>Txn ID</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Date & Time</th>
                    <th>Gateway Order</th>
                    <th>Gateway Payment</th>
                  </tr>
                </thead>

                <tbody>
                  {order.transactions.map(txn => (
                    <tr key={txn.id}>

                      <td>
                        <strong>#{txn.id}</strong>
                      </td>

                      <td>
                        ₹{parseFloat(txn.amount).toFixed(2)}
                      </td>

                      <td>
                        <span className="admin-payment-method">
                          {txn.paymentMethod?.toUpperCase()}
                        </span>
                      </td>

                      <td>
                        <span className={`txn-status-badge ${txn.paymentStatus}`}>
                          {txn.paymentStatus}
                        </span>
                      </td>

                      <td>
                        {new Date(txn.createdAt).toLocaleString('en-IN')}
                      </td>

                      <td>
                        <div className="admin-copy-field">
                          <span>{txn.gatewayOrderId}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(txn.gatewayOrderId);
                              alert('Copied Gateway Order ID');
                            }}
                          >
                            Copy
                          </button>
                        </div>
                      </td>

                      <td>
                        <div className="admin-copy-field">
                          <span>{txn.gatewayPaymentId}</span>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(txn.gatewayPaymentId);
                              alert('Copied Payment ID');
                            }}
                          >
                            Copy
                          </button>
                        </div>
                      </td>

                    </tr>
                  ))}
                </tbody>

              </table>

            </div>
          ) : (
            <div className="admin-empty-state">
              No transactions found for this order.
            </div>
          )}

        </div>

      </div>

    </div>
  );
};

export default OrderDetails;

